/** CSS variable set by WorkflowBuilderChrome for panel top offset. */
export const WORKFLOW_CHROME_HEIGHT_CSS_VAR = '--workflow-chrome-height';

/** Fallback toolbar height when the CSS variable is not yet measured (h-14 + h-11). */
export const WORKFLOW_CHROME_HEIGHT_DEFAULT = 100;

/** Default right editor / picker reserve width (matches 480px panels). */
export const WORKFLOW_EDITOR_WIDTH_DEFAULT = 480;

/** Wide editors: grouped actions, conditions, backend action editor. */
export const WORKFLOW_EDITOR_WIDTH_WIDE = 560;

/** Email action composer width. */
export const WORKFLOW_EDITOR_WIDTH_EMAIL = 640;

/** Platform inline action picker width. */
export const WORKFLOW_EDITOR_WIDTH_ACTIONS_PLATFORM = 400;

/** Agency action picker width (w-80). */
export const WORKFLOW_EDITOR_WIDTH_ACTIONS_AGENCY = 320;

/** Run test panel widths. */
export const WORKFLOW_EDITOR_WIDTH_RUN_TEST = 360;
export const WORKFLOW_EDITOR_WIDTH_RUN_TEST_EXPANDED = 720;

/** Minimum builder width before attempting reserved editor layout. */
export const MIN_RESERVED_BUILDER_WIDTH = 1280;

/** Minimum remaining canvas width required for reserved editor layout. */
export const MIN_COMFORTABLE_CANVAS_WIDTH = 900;

/** Responsive clamp bounds for standard docked editors. */
export const MIN_EDITOR_WIDTH = 360;
export const MAX_EDITOR_WIDTH = 480;

/** @deprecated Use MIN_RESERVED_BUILDER_WIDTH */
export const WORKFLOW_EDITOR_OVERLAY_BREAKPOINT = MIN_RESERVED_BUILDER_WIDTH;

/** @deprecated Use MIN_COMFORTABLE_CANVAS_WIDTH */
export const WORKFLOW_CANVAS_MIN_VISIBLE_WIDTH = MIN_COMFORTABLE_CANVAS_WIDTH;

/** @deprecated Use MIN_EDITOR_WIDTH */
export const WORKFLOW_EDITOR_WIDTH_MIN = MIN_EDITOR_WIDTH;

/** @deprecated Use MAX_EDITOR_WIDTH */
export const WORKFLOW_EDITOR_WIDTH_MAX = MAX_EDITOR_WIDTH;

const EDITOR_PANEL_FLAGS = [
  'showActionsPanel',
  'showTriggerPicker',
  'showEmailEditor',
  'showWaitEditor',
  'showSMSEditor',
  'showWebhookEditor',
  'showGrantPortalEditor',
  'showRevokePortalEditor',
  'showGrantOfferEditor',
  'showRevokeOfferEditor',
  'showApplyTagEditor',
  'showRemoveTagEditor',
  'showConditionEditor',
  'showNotificationEditor',
  'showBackendActionEditor',
  'showPreparePasswordResetEditor',
  'showFindUserPortalEditor',
  'showTriggerEditor',
  'showAuditLogEditor',
];

/**
 * True when any workflow editor or picker panel should reserve right-side space.
 */
export function isWorkflowEditorPanelOpen(flags = {}) {
  if (flags.showRunTestPanel) return true;
  return EDITOR_PANEL_FLAGS.some((key) => Boolean(flags[key]));
}

/**
 * Preferred editor width before responsive layout rules.
 */
export function resolvePreferredEditorWidth(flags = {}) {
  if (!isWorkflowEditorPanelOpen(flags)) return 0;

  if (flags.showRunTestPanel) {
    return flags.runTestExpanded
      ? WORKFLOW_EDITOR_WIDTH_RUN_TEST_EXPANDED
      : WORKFLOW_EDITOR_WIDTH_RUN_TEST;
  }

  if (flags.showEmailEditor) {
    return WORKFLOW_EDITOR_WIDTH_EMAIL;
  }

  if (
    flags.showPreparePasswordResetEditor
    || flags.showFindUserPortalEditor
    || flags.showConditionEditor
    || flags.showBackendActionEditor
  ) {
    return WORKFLOW_EDITOR_WIDTH_WIDE;
  }

  if (flags.showActionsPanel) {
    return flags.variant === 'agency'
      ? WORKFLOW_EDITOR_WIDTH_ACTIONS_AGENCY
      : WORKFLOW_EDITOR_WIDTH_ACTIONS_PLATFORM;
  }

  return WORKFLOW_EDITOR_WIDTH_DEFAULT;
}

/**
 * clamp(360px, 30vw, 480px) for standard docked editors.
 */
export function computeResponsiveStandardEditorWidth(viewportWidth) {
  const safeViewport = Math.max(0, Number(viewportWidth) || 0);
  const vwPortion = safeViewport * 0.3;
  return Math.round(
    Math.min(MAX_EDITOR_WIDTH, Math.max(MIN_EDITOR_WIDTH, vwPortion)),
  );
}

function isWideEditorWidth(width) {
  return width > MAX_EDITOR_WIDTH;
}

function resolveDockedEditorPanelWidth(preferredWidth, builderWidth, viewportWidth) {
  const standardWidth = computeResponsiveStandardEditorWidth(viewportWidth);

  if (!isWideEditorWidth(preferredWidth)) {
    return standardWidth;
  }

  const maxAllowed = Math.max(
    MIN_EDITOR_WIDTH,
    builderWidth - MIN_COMFORTABLE_CANVAS_WIDTH,
  );
  return Math.min(preferredWidth, maxAllowed);
}

/**
 * Decide whether the editor should use backdrop blur overlay styling.
 * Graph workspace width is never reserved; editor always overlays the canvas.
 */
export function shouldUseOverlayEditorMode(builderWidth, preferredEditorWidth) {
  const remainingCanvasWidth = builderWidth - preferredEditorWidth;
  return builderWidth < MIN_RESERVED_BUILDER_WIDTH
    || remainingCanvasWidth < MIN_COMFORTABLE_CANVAS_WIDTH;
}

/**
 * Fixed positioning below the workflow toolbar for right-side panels and backdrops.
 */
export function getWorkflowChromeOffsetStyle(chromeHeightPx) {
  if (chromeHeightPx != null && Number.isFinite(chromeHeightPx)) {
    const heightPx = Math.round(chromeHeightPx);
    return {
      top: `${heightPx}px`,
      height: `calc(100vh - ${heightPx}px)`,
    };
  }

  return {
    top: `var(${WORKFLOW_CHROME_HEIGHT_CSS_VAR}, ${WORKFLOW_CHROME_HEIGHT_DEFAULT}px)`,
    height: `calc(100vh - var(${WORKFLOW_CHROME_HEIGHT_CSS_VAR}, ${WORKFLOW_CHROME_HEIGHT_DEFAULT}px))`,
  };
}

/**
 * Build props for WorkflowEditorPanelShell from resolved layout state.
 */
export function buildWorkflowEditorPanelShellProps(layout, activePanelType, options = {}) {
  return {
    width: options.width ?? layout.editorPanelWidth,
    overlayMode: layout.editorOverlayMode,
    activePanelType,
    onOverlayDismiss: options.onOverlayDismiss,
    chromeHeight: options.chromeHeight,
  };
}

/**
 * Canvas wrapper style — width is controlled by flex layout in the workspace panel.
 */
export function getWorkflowCanvasLayoutStyle(_editorReserveWidth = 0) {
  return {
    width: '100%',
    maxWidth: '100%',
  };
}

/**
 * Resolve responsive editor layout for the workflow builder.
 */
export function resolveWorkflowEditorLayout(options = {}) {
  const {
    builderWidth = 1200,
    viewportWidth = builderWidth,
    ...flags
  } = options;

  if (!isWorkflowEditorPanelOpen(flags)) {
    return {
      editorReserveWidth: 0,
      editorOverlayMode: false,
      editorPanelWidth: 0,
    };
  }

  const preferredWidth = resolvePreferredEditorWidth(flags);
  const dockedWidth = resolveDockedEditorPanelWidth(
    preferredWidth,
    builderWidth,
    viewportWidth,
  );
  const overlayMode = shouldUseOverlayEditorMode(builderWidth, preferredWidth);
  const standardOverlayWidth = computeResponsiveStandardEditorWidth(viewportWidth);
  const overlayPanelWidth = isWideEditorWidth(preferredWidth)
    ? Math.min(
      preferredWidth,
      Math.max(MIN_EDITOR_WIDTH, builderWidth - MIN_COMFORTABLE_CANVAS_WIDTH),
    )
    : standardOverlayWidth;

  if (overlayMode) {
    return {
      editorReserveWidth: 0,
      editorOverlayMode: true,
      editorPanelWidth: overlayPanelWidth,
    };
  }

  return {
    editorReserveWidth: 0,
    editorOverlayMode: false,
    editorPanelWidth: dockedWidth,
  };
}

/**
 * Resolve how much horizontal space to reserve for the fixed right editor.
 * Returns 0 when no editor/picker is open or when overlay mode is active.
 */
export function resolveWorkflowEditorReserveWidth(flags = {}, layoutOptions = {}) {
  return resolveWorkflowEditorLayout({ ...flags, ...layoutOptions }).editorReserveWidth;
}
